import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Product } from 'app/models/product.models';
import { ArchivosService } from 'app/services/archivos.service';
import { ProductoService } from 'app/services/producto.service';
import Swal from 'sweetalert2';
import { UploadFileComponent } from '../../shared/upload-file/upload-file.component';
import { DialogLoadingComponent } from '../dialog-loading/dialog-loading.component';
import { DataService } from 'app/services/data.service';

@Component({
  selector: 'app-productos',
  templateUrl: './productos.component.html',
  styleUrls: ['./productos.component.css'],
})
export class ProductosComponent implements OnInit {
  // Clave para la última página visitada
  private readonly LAST_PAGE_STORAGE_KEY = 'productos_last_page';
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'code',
    'nombre',
    'codebarras',
    'type',
    'articlesType',
    'stock',
    'unidad',
    'precioventa',
    'accion',
  ];
  dataSource: MatTableDataSource<Product> = new MatTableDataSource();

  productos: Product[] = [];
  loading: boolean;
  paginaSiguiente = 1;
  totalArticulos = 10;
  itemXPag = 20; // Configurado para mostrar 20 registros por defecto
  search = '';
  lastKeypress: any;
  
  // Clave para localStorage
  private readonly PAGE_SIZE_STORAGE_KEY = 'productos_page_size';
  
  // Opciones para el selector de páginas
  pageSizeOptions: number[] = [10, 20, 25, 50, 100];
  
  // Para el ordenamiento
  sortColumn = '';
  sortDirection = '';

  // Bootstrap table properties
  currentSort = { column: '', direction: 'asc' };
  currentPage = 0;

  // Bootstrap table methods
  sortByColumn(column: string) {
    if (this.currentSort.column === column) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.column = column;
      this.currentSort.direction = 'asc';
    }
    
    // Apply sorting to dataSource
    const data = this.dataSource.data.slice();
    data.sort((a, b) => {
      const valueA = this.getColumnValue(a, column);
      const valueB = this.getColumnValue(b, column);
      
      if (valueA < valueB) {
        return this.currentSort.direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.currentSort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    this.dataSource.data = data;
  }

  getColumnValue(item: any, column: string): any {
    switch (column) {
      case 'name': return (item.name || '').toLowerCase();
      case 'code': return item.code || '';
      case 'barCode': return item.barCode || '';
      case 'type': return item.type || '';
      case 'stock': return item.stock?.quantity !== undefined ? item.stock.quantity : item.quantity || 0;
      case 'salePrice': return item.salePrice || 0;
      default: return '';
    }
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.getTotalPages()) {
      this.currentPage = page;
      // Implement pagination logic here if needed
      this.page({ pageIndex: page, pageSize: this.itemXPag, length: this.totalArticulos });
    }
  }

  getTotalPages(): number {
    return Math.ceil(this.totalArticulos / this.itemXPag);
  }

  getVisiblePages(): number[] {
    const totalPages = this.getTotalPages();
    const maxVisible = 5;
    const pages: number[] = [];
    
    let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible);
    
    if (end - start < maxVisible) {
      start = Math.max(0, end - maxVisible);
    }
    
    for (let i = start; i < end; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  getDisplayRange(): string {
    const start = this.currentPage * this.itemXPag + 1;
    const end = Math.min((this.currentPage + 1) * this.itemXPag, this.totalArticulos);
    return `${start}-${end}`;
  }

  onPageSizeChange() {
    // Guardar el pageSize actualizado en localStorage
    this.savePageSizeToStorage(this.itemXPag);
    
    this.currentPage = 0;
    this.goToPage(0);
  }

  // Método para manejar eventos del paginador de Material
  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemXPag = event.pageSize;
    
    // Guardar el pageSize en localStorage
    this.savePageSizeToStorage(event.pageSize);
  // Guardar la última página visitada en localStorage
  this.saveLastPageToStorage(event.pageIndex);
    
    this.page({ pageIndex: event.pageIndex, pageSize: event.pageSize, length: this.totalArticulos });
  }
  // Guardar la última página visitada
  private saveLastPageToStorage(pageIndex: number): void {
    try {
      localStorage.setItem(this.LAST_PAGE_STORAGE_KEY, pageIndex.toString());
    } catch (error) {
      console.warn('Error al guardar la última página en localStorage:', error);
    }
  }

  // Restaurar la última página visitada
  private loadLastPageFromStorage(): void {
    try {
      const savedPage = localStorage.getItem(this.LAST_PAGE_STORAGE_KEY);
      if (savedPage !== null) {
        const page = parseInt(savedPage, 10);
        if (!isNaN(page)) {
          this.currentPage = page;
        }
      }
    } catch (error) {
      console.warn('Error al cargar la última página desde localStorage:', error);
    }
  }

  // Métodos para gestionar localStorage
  private loadPageSizeFromStorage(): void {
    try {
      const savedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);
      if (savedPageSize) {
        const pageSize = parseInt(savedPageSize, 10);
        // Verificar que el pageSize guardado esté en las opciones válidas
        if (this.pageSizeOptions.includes(pageSize)) {
          this.itemXPag = pageSize;
        }
      }
    } catch (error) {
      console.warn('Error al cargar pageSize desde localStorage:', error);
    }
  }

  private savePageSizeToStorage(pageSize: number): void {
    try {
      localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, pageSize.toString());
    } catch (error) {
      console.warn('Error al guardar pageSize en localStorage:', error);
    }
  }

  // Navegación para editar producto desde la fila
  navigateToEdit(productId: string, event: Event) {
    // Solo navegar si no se hizo clic en el dropdown
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.router.navigate(['/productos/edit/', productId]);
    }
  }

  // Navegación para editar producto desde el dropdown
  editProduct(productId: string) {
    this.router.navigate(['/productos/edit/', productId]);
  }

  constructor(
    private productoService: ProductoService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private router: Router,
    private archivoServicio: ArchivosService,
    public dataService: DataService
  ) {}

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Productos y Servicios';
    }
    
    // Restaurar el pageSize guardado en localStorage
    this.loadPageSizeFromStorage();
  // Restaurar la última página visitada
  this.loadLastPageFromStorage();
    
    this.getProductos();
  }

  ngAfterViewInit() {
    // Configurar paginador y sort solo si están disponibles
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.pageSize = this.itemXPag;
    }
    
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    
    // Configurar el ordenamiento personalizado para campos específicos
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {
      switch (sortHeaderId) {
        case 'nombre': 
          return (data.name || '').toLowerCase();
        case 'code': 
          return (data.code || '').toLowerCase();
        case 'codebarras': 
          return (data.barCode || '').toLowerCase();
        case 'type': 
          return (data.type || '').toLowerCase();
        case 'stock': 
          const stockValue = data.stock?.quantity !== undefined ? data.stock.quantity : (data.quantity || 0);
          return parseFloat(String(stockValue)) || 0;
        case 'precioventa': 
          const priceValue = data.salePrice || 0;
          return parseFloat(String(priceValue)) || 0;
        case 'unidad': 
          return ''; // Se mostrará desde el template, ordenamiento básico
        case 'articlesType': 
          return ''; // Se mostrará desde el template, ordenamiento básico
        default: 
          return data[sortHeaderId] || '';
      }
    };

    // Sincronizar el paginador con los datos iniciales (solo si existe)
    if (this.paginator) {
      this.paginator.pageSize = this.itemXPag;
      this.paginator.pageIndex = this.paginaSiguiente - 1; // Angular Material usa índice basado en 0
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    
    // Limpiar caracteres especiales que añaden los lectores de código de barras
    this.search = filterValue
      .trim() // Eliminar espacios al inicio y final
      .replace(/[\r\n\t\f\v\0]/g, '') // Eliminar todos los caracteres de control
      .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control ASCII
      .replace(/\s+/g, ' ') // Normalizar espacios múltiples
      .trim() // Limpiar espacios resultantes
      .toLowerCase();
    
    if (event.timeStamp - this.lastKeypress > 200) {
      // Resetear la paginación al primer elemento
      this.paginaSiguiente = 1;
      
      // Asegurar que el paginador regrese a la primera página
      if (this.paginator) {
        this.paginator.pageIndex = 0;
        // Forzar la actualización del paginador
        this.paginator.firstPage();
      }
      
      // Obtener los productos con el nuevo filtro
      this.getProductos();
    }
    this.lastKeypress = event.timeStamp;
  }

  onPaste(event: ClipboardEvent) {
    // Prevenir el comportamiento por defecto
    event.preventDefault();
    
    // Obtener el texto del clipboard
    const pastedText = event.clipboardData?.getData('text') || '';
    
    // Limpiar el texto de caracteres especiales
    const cleanedText = pastedText
      .trim()
      .replace(/[\r\n\t\f\v\0]/g, '') // Eliminar todos los caracteres de control
      .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control ASCII
      .replace(/\s+/g, ' ') // Normalizar espacios múltiples
      .trim(); // Limpiar espacios resultantes
    
    // Actualizar el input
    const input = event.target as HTMLInputElement;
    input.value = cleanedText;
    
    // Aplicar el filtro
    this.search = cleanedText.toLowerCase();
    this.paginaSiguiente = 1;
    
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.firstPage();
    }
    
    this.getProductos();
  }

  getProductos() {
    this.loading = true;
    this.productos = [];
    this.dataSource.data = [];
    this.productoService
      .getArticulos(this.paginaSiguiente, this.itemXPag, this.search)
      .subscribe(
        res => {
          this.productos = res.data;
          this.dataSource.data = this.productos;
          
          // Reconfigurar el ordenamiento después de cargar los datos
          if (this.sort) {
            this.dataSource.sort = this.sort;
            if (this.sortColumn && this.sortDirection) {
              this.sort.active = this.sortColumn;
              this.sort.direction = this.sortDirection as 'asc' | 'desc';
            }
          }
          
          // Actualizar el total y sincronizar el paginador
          this.totalArticulos = res.total;
          
          // Asegurar que el paginador esté sincronizado
          if (this.paginator) {
            this.paginator.length = this.totalArticulos;
            this.paginator.pageSize = this.itemXPag;
            this.paginator.pageIndex = this.paginaSiguiente - 1;
          }
          
          this.loading = false;
        },
        err => {
          this.loading = false;
          console.error('Error al cargar productos:', err);
        }
      );
  }

  page(event) {
    this.itemXPag = event.pageSize; // Actualizar el tamaño de página
    this.paginaSiguiente = event.pageIndex + 1; // Angular Material usa índice basado en 0, pero el API espera basado en 1
    this.getProductos();
  }

  // Método para manejar el ordenamiento
  sortData(sort: Sort) {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.paginaSiguiente = 1; // Resetear a la primera página
    
    // Resetear el paginador al cambiar el ordenamiento
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.firstPage();
    }
    
    this.getProductos();
  }

  openDialog(producto?: Product) {
    this.router.navigate(['/productos', 'create']);
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  actualizarPreciosMasivos() {
    this.router.navigateByUrl('/productos/precios/masivos');
  }

  /**
   * Descargar un excel con los articulos que tiene el sistema
   */
  descargarExcel() {
    const fileName = `articulos_${new Date().getDate()}${
      new Date().getUTCMonth() + 1
    }${new Date().getFullYear()}${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}.xlsx`;

    // Mostrar diálogo de espera profesional
    Swal.fire({
      title: 'Generando archivo Excel',
      html: `
        <p style="margin-top: 20px; color: #66615b; font-size: 14px;">
          Procesando información de artículos...<br>
          <small>Por favor espere, esto puede tomar unos momentos</small>
        </p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.archivoServicio.getArticulosExcel().subscribe(
      response => {
        Swal.close();
        this.manageExcelFile(response, fileName);
        
        // Mostrar mensaje de éxito
        Swal.fire({
          icon: 'success',
          title: 'Descarga completada',
          text: 'El archivo Excel se ha descargado correctamente',
          timer: 2000,
          showConfirmButton: false
        });
      },
      err => {
        Swal.close();
        console.log(err);
        
        // Mostrar mensaje de error profesional
        Swal.fire({
          icon: 'error',
          title: 'Error al descargar',
          text: 'Ocurrió un error al generar el archivo Excel. Por favor, intente nuevamente.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#102dd3'
        });
      }
    );
  }

  manageExcelFile(response: any, fileName: string): void {
    const dataType = response.type;
    const binaryData = [];
    binaryData.push(response);

    const filtePath = window.URL.createObjectURL(
      new Blob(binaryData, { type: dataType })
    );
    const downloadLink = document.createElement('a');
    downloadLink.href = filtePath;
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  cargarExcel() {
    // Navegar a la ruta de carga masiva de productos
    this.router.navigate(['/productos/carga-excel']);
  }

  eliminar(producto: Product, event) {
    //para evitar que entre en el producto

    event.preventDefault();
    event.stopPropagation();
    Swal.fire({
      title: `¿Desea eliminar el ${producto.type}?`,
      text: `${producto.name}`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminalo!',
      cancelButtonText: 'No',
    }).then(result => {
      if (result.isConfirmed) {
        this.productoService.eliminarArticulo(producto.id).subscribe(
          data => {
            this.productos = data;
            this.getProductos();
            this.openSnackBar(`${producto.type} eliminado correctamente`, 'Información');
          },
          err => {
            console.log(err);
            this.openSnackBar(`Error al eliminar el ${producto.type}`, 'Información');
          }
        );
      }
    });
  }
}
